import { AxiosResponse } from 'axios';

import { FormTechnicalType } from 'src/common/forms/TechnicalTypeEditorForm';
import { ICompetition } from 'src/interfaces/Competition';
import { TechnicalType } from 'src/interfaces/TechnicalType';

export interface TechnicalTypeCrudPaths {
  list: string;
  save: string;
  update: (id: string) => string;
  delete: (id: string) => string;
  bulkDelete: string;
}

export interface TechnicalTypeCrudStateKeys {
  list: 'distances' | 'accommodations' | 'competitions';
  byId: 'distancesById' | 'accommodationsById' | 'competitionsById';
  byName: 'distancesByName' | 'accommodationsByName' | 'competitionsByName';
}

export interface TechnicalTypeCrudActionNames {
  fetch: string;
  save: string;
  delete: string;
  deleteAll: string;
  reset: string;
  dispatch: string;
}

export interface TechnicalTypeCrudMswConfig {
  mockDbKey: 'distances' | 'accommodations' | 'competitions';
  nextIdPrefix: 'distance' | 'accommodation' | 'competition';
}

// TItem is a phantom type used by TechnicalTypeCrudImpl (bound at config declaration sites).
export interface TechnicalTypeCrudConfig<TItem = unknown> {
  paths: TechnicalTypeCrudPaths;
  stateKeys: TechnicalTypeCrudStateKeys;
  actions: TechnicalTypeCrudActionNames;
  msw: TechnicalTypeCrudMswConfig;
  /** Phantom so TItem is referenced for callers / TechnicalTypeCrudImpl. */
  readonly __itemType?: TItem;
}

export interface DistanceSlice {
  distances: TechnicalType[];
  distancesById?: { [key: string]: TechnicalType };
  distancesByName?: { [key: string]: TechnicalType };
  isLoading: boolean;
  deleteDistance: (id: string) => Promise<void | AxiosResponse<null>>;
  deleteAllDistances: () => Promise<void | AxiosResponse<null>>;
  fetchDistances: () => Promise<void | AxiosResponse<null>>;
  resetDistances: () => void;
  saveDistance: (
    distance: FormTechnicalType,
    id?: string,
  ) => Promise<void | AxiosResponse<TechnicalType[]>>;
}

export interface AccommodationSlice {
  accommodations: TechnicalType[];
  accommodationsById?: { [key: string]: TechnicalType };
  accommodationsByName?: { [key: string]: TechnicalType };
  isLoading: boolean;
  deleteAccommodation: (id: string) => Promise<void | AxiosResponse<null>>;
  deleteAllAccommodations: () => Promise<void | AxiosResponse<null>>;
  fetchAccommodations: () => Promise<void | AxiosResponse<null>>;
  resetAccommodations: () => void;
  saveAccommodation: (
    accommodation: FormTechnicalType,
    id?: string,
  ) => Promise<void | AxiosResponse<TechnicalType[]>>;
}

export interface CompetitionSlice {
  competitions: ICompetition[];
  competitionsById?: { [key: string]: ICompetition };
  competitionsByName?: { [key: string]: ICompetition };
  isLoading: boolean;
  deleteCompetition: (id: string) => Promise<void | AxiosResponse<null>>;
  deleteAllCompetitions: () => Promise<void | AxiosResponse<null>>;
  fetchCompetitions: () => Promise<void | AxiosResponse<null>>;
  resetCompetitions: () => void;
  saveCompetition: (
    competition: FormTechnicalType,
    id?: string,
  ) => Promise<void | AxiosResponse<ICompetition[]>>;
}

export const DISTANCES_CRUD_CONFIG: TechnicalTypeCrudConfig<TechnicalType> = {
  paths: {
    list: 'distances/',
    save: 'distances/save',
    update: id => `distances/update/${id}`,
    delete: id => `distances/delete/${id}`,
    bulkDelete: 'distances/delete/bulk',
  },
  stateKeys: {
    list: 'distances',
    byId: 'distancesById',
    byName: 'distancesByName',
  },
  actions: {
    fetch: 'fetchDistances',
    save: 'saveDistance',
    delete: 'deleteDistance',
    deleteAll: 'deleteAllDistances',
    reset: 'resetDistances',
    dispatch: 'internal_dispatchDistances',
  },
  msw: {
    mockDbKey: 'distances',
    nextIdPrefix: 'distance',
  },
};

export const ACCOMMODATIONS_CRUD_CONFIG: TechnicalTypeCrudConfig<TechnicalType> = {
  paths: {
    list: 'accommodations/',
    save: 'accommodations/save',
    update: id => `accommodations/update/${id}`,
    delete: id => `accommodations/delete/${id}`,
    bulkDelete: 'accommodations/delete/bulk',
  },
  stateKeys: {
    list: 'accommodations',
    byId: 'accommodationsById',
    byName: 'accommodationsByName',
  },
  actions: {
    fetch: 'fetchAccommodations',
    save: 'saveAccommodation',
    delete: 'deleteAccommodation',
    deleteAll: 'deleteAllAccommodations',
    reset: 'resetAccommodations',
    dispatch: 'internal_dispatchAccommodations',
  },
  msw: {
    mockDbKey: 'accommodations',
    nextIdPrefix: 'accommodation',
  },
};

export const COMPETITIONS_CRUD_CONFIG: TechnicalTypeCrudConfig<ICompetition> = {
  paths: {
    list: 'competitions/',
    save: 'competitions/save',
    update: id => `competitions/update/${id}`,
    delete: id => `competitions/delete/${id}`,
    bulkDelete: 'competitions/delete/bulk',
  },
  stateKeys: {
    list: 'competitions',
    byId: 'competitionsById',
    byName: 'competitionsByName',
  },
  actions: {
    fetch: 'fetchCompetitions',
    save: 'saveCompetition',
    delete: 'deleteCompetition',
    deleteAll: 'deleteAllCompetitions',
    reset: 'resetCompetitions',
    dispatch: 'internal_dispatchCompetitions',
  },
  msw: {
    mockDbKey: 'competitions',
    nextIdPrefix: 'competition',
  },
};
